import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchMarketingReport, MarketingReport } from '@/lib/api/marketing-report-service';

interface UseMarketingReportState {
  data: MarketingReport | null;
  loading: boolean;
  error: string | null;
}

export function useMarketingReport() {
  const [state, setState] = useState<UseMarketingReportState>({
    data: null,
    loading: false,
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const load = useCallback(async (start: string, end: string, refresh = false) => {
    // Cancel any in-flight request before starting a new one
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetchMarketingReport(start, end, refresh, controller.signal);
      if (!controller.signal.aborted) {
        setState({ data, loading: false, error: null });
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (controller.signal.aborted) return; // newer load already started — discard stale error
      setState(s => ({ ...s, loading: false, error: err.message || 'Failed to load report' }));
    }
  }, []);

  return { ...state, load };
}
